import { useEffect } from 'react'
import type { ReactElement } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'

import { AuthProvider } from './lib/auth'
import { RangeProvider } from './lib/range'
import { bootBranding } from './lib/branding'
import { queryClient } from './lib/query'
import { LocaleProvider } from './lib/i18n'
import { ToastProvider } from './components/ui/Toast'
import { TourProvider } from './features/tours/store'
import { router } from './router'

/**
 * Provider order matters:
 *   QueryClient → Locale → Toast → Auth → Range → Tour → Router
 *
 * Auth reads brands through TanStack Query and reports failures through toasts, so both must
 * already exist. Range and Tour only need to sit above the router, which is where the picker and
 * `TourPilot` (`features/tours`) live — a guided tour changes route itself, so its state has to
 * survive the navigation it causes.
 *
 * Locale sits above Toast and Auth because both produce copy: a toast raised during sign-in has
 * to be in the reader's language, and `AuthProvider` hands the account's `locale` to the store
 * as soon as the session resolves. The hooks read the store directly and work without the
 * provider — it adds the saving state the switcher shows and re-asserts `<html lang>` — so this
 * is what completes the switcher, not what makes translation work.
 */
const App = (): ReactElement => {
  useEffect(() => {
    // Tenant palette, logo, favicon and title. Fire-and-forget: the stock tokens are already
    // correct, so there is no reason to hold the first paint on a network round trip.
    void bootBranding()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <ToastProvider>
          <AuthProvider>
            <RangeProvider>
              <TourProvider>
                <RouterProvider router={router} />
              </TourProvider>
            </RangeProvider>
          </AuthProvider>
        </ToastProvider>
      </LocaleProvider>
    </QueryClientProvider>
  )
}

export default App
